import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'

import { AsyncStateN } from '@blade/std-lib'
import { delugeIdAtom } from '@root/entities/auth/atoms'
import { UserAvatar } from '@root/entities/auth/ui/user-avatar'
import { Deeplink, DeeplinkURL } from '@root/entities/deeplinking'
import { UserShort } from '@root/entities/user-short'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { commentsHistoryTypeAtom } from '@root/features/comment/atoms/comments-history'
import { useAppRoutes } from '@root/features/routing'
import { profileBoardAtom } from '@root/features/user/profile/atoms/reatom-profile-boards'
import { openEditProfileFormModalEvent } from '@root/features/user/profile-info/atoms/reatom-edit-profile-form'
import { EditProfileModal } from '@root/features/user/profile-info/edit-profile-modal'
import { useClipboard } from '@root/shared'

import { ProfileHeader as ProfileHeader_ } from '../common/profile-header'
import { ProfileHeaderSkeleton } from '../common/profile-header-skeleton'
import { ProfileRankTags } from '../common/profile-rank-tags'

import type { ProfileHeaderAction } from '../common/profile-header'
import type { Comment } from '@root/entities/comment'
import type { Profile } from '@root/entities/profile/model'
import type { DelugeId } from '@root/entities/user-short/model'

type Props = {
  delugeId: DelugeId
  profileState: AsyncStateN.AsyncStateN<unknown, Profile.Profile>
}

// TODO: Merge with MyProfileHeader
export const UserProfileHeader: React.FC<Props> = ({
  profileState,
  delugeId,
}: Props) => {
  const [currentDelugeId] = useAtom(delugeIdAtom)
  const [_, setProfileBoard] = useAtom(profileBoardAtom)
  const [_type, setCommentsHistoryType] = useAtom(commentsHistoryTypeAtom)

  const { copyToClipboard } = useClipboard()
  const userProfile = profileState.value
  const openEditProfileModal = useAction(openEditProfileFormModalEvent)

  const isSelfProfile = delugeId === currentDelugeId
  const profile = profileState.value

  const { navigateToProfileTradingLeaderboard } = useAppRoutes()

  const isLoading = useMemo(
    () =>
      AsyncStateN.matchWaiting(profileState, {
        Waiting: () => true,
        Error: () => false,
        Success: () => false,
      }),
    [profileState],
  )

  const handleFollowersClick = () => {
    setProfileBoard('followers')
  }
  const handleFollowingsClick = () => {
    setProfileBoard('following')
  }
  const handleCoinsClick = () => {
    setProfileBoard('coins')
  }
  const handleCommentsClick = (commentType: Comment.CommentType) => {
    setProfileBoard('comments')
    setCommentsHistoryType(commentType)
  }

  const actions: Array<ProfileHeaderAction> = []

  if (delugeId) {
    const url = DeeplinkURL.fromDeeplink(
      new Deeplink.UserProfile({ delugeId: delugeId }),
    )

    actions.push({
      icon: 'share',
      onClick: () => copyToClipboard(url.toString()),
    })
  }

  if (isSelfProfile) {
    actions.push({
      icon: 'pen',
      onClick: () => openEditProfileModal(),
    })
  }

  if (isLoading) {
    return <ProfileHeaderSkeleton />
  }

  return (
    <>
      <ProfileHeader_
        actions={actions}
        onAvatarClick={() => setProfileBoard('statistics')}
        avatar={
          isSelfProfile ? (
            <UserAvatar
              withPreview={false}
              outlineWidth={1.5}
              size={60}
              withHypedLetter
            />
          ) : (
            <UserShortAvatar
              withPreview={false}
              userShort={profile}
              size={60}
              withHypedLetter
            />
          )
        }
        title={profile ? UserShort.someName(profile) : ''}
        mainSubtitle={
          <ProfileRankTags
            trading={{
              count: userProfile?.tradeRank || '-', // 0 and undefined
              onClick: isSelfProfile
                ? navigateToProfileTradingLeaderboard
                : undefined,
            }}
            referral={{
              count: userProfile?.referralRank || '-', // 0 and undefined
              onClick: isSelfProfile
                ? navigateToProfileTradingLeaderboard
                : undefined,
            }}
          />
        }
        connections={{
          onFollowersClick: handleFollowersClick,
          onFollowingsClick: handleFollowingsClick,
          followers: userProfile?.followers.length ?? '-',
          following: userProfile?.following.length ?? '-',
        }}
        counts={{
          onLikesClick: () => handleCommentsClick('comments'),
          onMentionsClick: () => handleCommentsClick('replies'),
          onCoinsClick: handleCoinsClick,
          coins: userProfile?.deployedTokensCount ?? '-',
          likes: userProfile?.likesCount ?? '-',
          mentions: userProfile?.mentionsCount ?? '-',
        }}
      />
      <EditProfileModal />
    </>
  )
}
